import { useEffect, useState } from "react";

/**
 * Runs an async read once on mount and reports its result.
 *
 * These pages used to be React Server Components: they awaited their data in
 * the component body and the server sent finished HTML. In the SPA the same
 * reads happen in the browser, so each one needs a loading state that the
 * markup can distinguish from a result.
 *
 * `undefined` means "still reading" and is deliberately not the same as a
 * resolved `null`. Several of these reads answer `null` for "the chain could
 * not be reached", and a page that rendered that message while the request was
 * still in flight would be announcing an outage that has not happened.
 */
export function useAsync<T>(read: () => Promise<T>, deps: unknown[] = []): T | undefined {
  const [value, setValue] = useState<T | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void read().then((result) => {
      if (!cancelled) setValue(result);
    });
    return () => {
      cancelled = true;
    };
    // The caller owns the dependency list, exactly as it would for useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return value;
}
